import { format as formatWithPattern } from "date-fns";

export type DisplayFormat = "eu" | "us";

const STORAGE_KEY = "display_format";

let currentFormat: DisplayFormat = readStoredFormat();

function readStoredFormat(): DisplayFormat {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "us" || stored === "eu") {
      return stored;
    }
  } catch {
    // localStorage can be unavailable; EU stays the default.
  }
  return "eu";
}

export function getDisplayFormat(): DisplayFormat {
  return currentFormat;
}

export function setDisplayFormat(format: DisplayFormat) {
  currentFormat = format;
  try {
    window.localStorage.setItem(STORAGE_KEY, format);
  } catch {
    // Ignore storage failures; the in-memory value still applies this session.
  }
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

function groupDigits(digits: string, separator: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

function separators() {
  if (currentFormat === "us") {
    return { thousand: ",", decimal: "." };
  }
  return { thousand: ".", decimal: "," };
}

export function formatInteger(value: unknown): string {
  if (isEmpty(value)) {
    return "";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  const negative = numeric < 0;
  const digits = Math.trunc(Math.abs(numeric)).toString();
  const grouped = groupDigits(digits, separators().thousand);
  return negative ? `-${grouped}` : grouped;
}

export function formatDecimal(value: unknown, digits = 2): string {
  if (isEmpty(value)) {
    return "";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  const negative = numeric < 0;
  const fixed = Math.abs(numeric).toFixed(digits);
  const [intPart, frac = ""] = fixed.split(".");
  const { thousand, decimal } = separators();
  const grouped = groupDigits(intPart, thousand);
  const body = digits > 0 ? `${grouped}${decimal}${frac}` : grouped;
  return negative ? `-${body}` : body;
}

export function formatPercent(value: unknown, digits = 2): string {
  const formatted = formatDecimal(value, digits);
  if (!formatted) {
    return "";
  }
  return `${formatted}%`;
}

// Compact magnitude for heatmap cells, using the active display-format separators.
export function formatCompact(value: unknown): string {
  if (isEmpty(value)) {
    return "";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  const negative = numeric < 0;
  const abs = Math.abs(numeric);
  const units = [
    { limit: 1_000_000_000, suffix: "B" },
    { limit: 1_000_000, suffix: "M" },
    { limit: 1_000, suffix: "K" },
  ];
  const unit = units.find((item) => abs >= item.limit);
  if (!unit) {
    return formatInteger(numeric);
  }
  const scaled = abs / unit.limit;
  const digits = scaled >= 100 ? 0 : 1;
  let body = formatDecimal(scaled, digits);
  const decimal = separators().decimal;
  if (digits === 1 && body.endsWith(`${decimal}0`)) {
    body = body.slice(0, -(decimal.length + 1));
  }
  return `${negative ? "-" : ""}${body}${unit.suffix}`;
}

export function roundToHundred(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const negative = value < 0;
  const rounded = Math.round(Math.abs(value) / 100) * 100;
  return negative ? -rounded : rounded;
}

// formatSearchVolume rounds to the nearest hundred, then uses a lowercase
// magnitude suffix. 21100 is 21,1k in the EU format and 21.1k in the US format.
export function formatSearchVolume(value: unknown): string {
  if (isEmpty(value)) {
    return "";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  const rounded = roundToHundred(numeric);
  const negative = rounded < 0;
  const abs = Math.abs(rounded);
  const units = [
    { limit: 1_000_000_000, suffix: "b" },
    { limit: 1_000_000, suffix: "m" },
    { limit: 1_000, suffix: "k" },
  ];
  const unit = units.find((item) => abs >= item.limit);
  if (!unit) {
    return formatInteger(rounded);
  }
  const scaled = abs / unit.limit;
  const digits = scaled >= 100 ? 0 : 1;
  let body = formatDecimal(scaled, digits);
  const decimal = separators().decimal;
  if (digits === 1 && body.endsWith(`${decimal}0`)) {
    body = body.slice(0, -(decimal.length + 1));
  }
  return `${negative ? "-" : ""}${body}${unit.suffix}`;
}

export function datePattern(): string {
  return currentFormat === "us" ? "MM/dd/yyyy" : "dd.MM.yyyy";
}

export function shortDatePattern(): string {
  return currentFormat === "us" ? "MM/dd/yy" : "dd.MM.yy";
}

export function monthYearPattern(): string {
  return currentFormat === "us" ? "MM/yy" : "MM.yy";
}

function toDate(value: string | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

export function formatIsoDate(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) {
    return formatDate(value);
  }
  const [, year, month, day] = match;
  return currentFormat === "us" ? `${month}/${day}/${year}` : `${day}.${month}.${year}`;
}

export function formatShortIsoDate(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) {
    return formatDate(value);
  }
  const [, year, month, day] = match;
  const shortYear = year.slice(2);
  return currentFormat === "us" ? `${month}/${day}/${shortYear}` : `${day}.${month}.${shortYear}`;
}

// Day and month only. EU is dd.MM, US is MM/dd. The year stays off the column header.
export function formatDayMonth(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) {
    return formatDate(value);
  }
  const [, , month, day] = match;
  return currentFormat === "us" ? `${month}/${day}` : `${day}.${month}`;
}

// Chart tooltips keep this English set even when the account date format is EU.
const ENGLISH_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function englishWeekday(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((value || "").trim());
  if (!match) {
    return "";
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(utc.getTime()) ||
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return "";
  }
  return ENGLISH_WEEKDAYS[utc.getUTCDay()];
}

// Hover form of a day column. EU keeps the ordinal dot: 24.10. (Sat). US is 10/24 (Sat).
// A value that is not a real calendar day stays as formatDayMonth, without a weekday.
export function formatDayMonthWeekday(value: string | null | undefined): string {
  const formatted = formatDayMonth(value);
  const weekday = englishWeekday(value);
  if (!formatted || !weekday) {
    return formatted;
  }
  const dated = currentFormat === "us" ? formatted : `${formatted}.`;
  return `${dated} (${weekday})`;
}

export function appendEnglishWeekday(formatted: string, iso: string | null | undefined): string {
  const weekday = englishWeekday(iso);
  if (!formatted || !weekday) {
    return formatted;
  }
  return `${formatted} (${weekday})`;
}

export function formatDailyTooltipDate(value: string | null | undefined): string {
  return appendEnglishWeekday(formatShortIsoDate(value), value);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }
  const date = toDate(value);
  if (!date) {
    return "";
  }
  return formatWithPattern(date, datePattern());
}

export function formatDateTime(
  value: string | Date | null | undefined,
  timeZone?: string
): string {
  if (value == null || value === "") {
    return "";
  }
  const date = toDate(value);
  if (!date) {
    return "";
  }
  if (timeZone) {
    return new Intl.DateTimeFormat(currentFormat === "us" ? "en-US" : "de-DE", {
      timeZone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: currentFormat === "us" ? "numeric" : "2-digit",
      minute: "2-digit",
      hour12: currentFormat === "us",
    }).format(date);
  }
  const pattern = currentFormat === "us" ? "MM/dd/yyyy, h:mm a" : "dd.MM.yyyy, HH:mm";
  return formatWithPattern(date, pattern);
}

export const thousands = (value: any) => {
  if (value === undefined) return 0;
  return formatInteger(value);
};

export const percentage = (value: number) => {
  return formatPercent(value, 2);
};

export const ucfirst = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const limitLength = (value: string, limit: number) => {
  if (value.length > limit) {
    return value.substring(0, limit) + "...";
  }
  return value;
};

export const stripHostname = (url: string) => {
  let parser = document.createElement("a");
  parser.href = url;
  return parser.pathname;
};

export const wordCount = (value: string) => {
  return value.split(" ").length;
};

export const rounded = (value: any, precision: number) => {
  return Number.parseFloat(value).toPrecision(precision);
};

export const calendarDate = (value: string) => {
  return formatWithPattern(new Date(value), "yyyy-MM-dd");
};
